// src/shared/api/labourLogService.js
import axios from "axios";

const BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const LABOUR_BASE_URL = `${BASE}/api/admin/labour`;
const BATCHES_URL = `${BASE}/api/public/batches/all`;

/** Extract readable error message from backend */
const apiMsg = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.error ||
  (typeof e?.response?.data === "string" ? e.response.data : null) ||
  e?.message ||
  "Request failed";

/** Creates a reusable Axios instance with the authorization token. */
const createApiClient = (baseURL) => {
  const token = localStorage.getItem("authToken");
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  return axios.create({ baseURL, headers });
};

const labourApi = () => createApiClient(LABOUR_BASE_URL);

/** sanitize helper so we don't send ?x=undefined etc. */
const cleanParams = (params = {}) => {
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (
      v === undefined ||
      v === null ||
      v === "" ||
      v === "undefined" ||
      v === "null"
    )
      return;
    clean[k] = v;
  });
  return clean;
};

const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const normalizeSalaryType = (t) => {
  if (!t) return null;
  const s = String(t).trim().toUpperCase();
  if (["DAILY", "WEEKLY", "MONTHLY"].includes(s)) return s;
  return null;
};

/** Normalize worker coming from backend into UI-friendly shape */
const normalizeWorkerFromApi = (w = {}) => {
  const name = w.name ?? w.workerName ?? w.worker_name ?? w.fullName ?? "";
  const labourId = w.labourId ?? w.labourID ?? w.labour_id ?? w.code ?? null;

  return {
    ...w,
    name,
    labourId,
    active: w.active !== false,
    // NEW fields (safe)
    salaryType: w.salaryType ?? null,
    salaryAmount: toNumberOrNull(w.salaryAmount),
  };
};

/** Normalize Worker payload -> backend-friendly */
const normalizeWorkerPayload = (payload = {}) => {
  const out = { ...payload };

  const name =
    typeof out.name === "string"
      ? out.name.trim()
      : typeof out.workerName === "string"
      ? out.workerName.trim()
      : "";
  if (name) {
    out.name = name;
    out.workerName = name; // safe fallback if backend uses workerName
  }

  if (typeof out.mobile === "string") out.mobile = out.mobile.trim();
  if (typeof out.notes === "string") out.notes = out.notes.trim();

  if (typeof out.active === "string") {
    if (out.active.toLowerCase() === "true") out.active = true;
    if (out.active.toLowerCase() === "false") out.active = false;
  }
  if (out.active === "") delete out.active;

  // ✅ NEW salary fields
  out.salaryType = normalizeSalaryType(out.salaryType) ?? undefined;
  out.salaryAmount = toNumberOrNull(out.salaryAmount);

  // If salaryAmount null, don't send (so backend keeps existing/default)
  if (out.salaryAmount === null) delete out.salaryAmount;
  if (out.salaryType === undefined) delete out.salaryType;

  return out;
};

/** Normalize WorkLog payload -> backend-friendly */
const normalizeWorkLogPayload = (payload = {}) => {
  const out = { ...payload };

  if (out.workerId === "" || out.workerId === undefined) out.workerId = null;
  if (typeof out.workerId === "string" && out.workerId.trim() !== "")
    out.workerId = Number(out.workerId);

  if (Array.isArray(out.batchIds)) {
    out.batchIds = out.batchIds
      .filter((x) => x !== "" && x !== null && x !== undefined)
      .map((x) => Number(x));
  }

  const toISODate = (d) => {
    if (!d) return null;
    if (typeof d === "string") return d.slice(0, 10);
    try {
      return new Date(d).toISOString().slice(0, 10);
    } catch {
      return null;
    }
  };

  out.fromDate = toISODate(out.fromDate);
  out.toDate = toISODate(out.toDate);

  // ✅ salary controls (optional)
  out.salaryType = normalizeSalaryType(out.salaryType) ?? undefined;

  out.perDaySalary = toNumberOrNull(out.perDaySalary);
  out.salaryAmount = toNumberOrNull(out.salaryAmount);

  // ✅ NEW leaveDays
  out.leaveDays = out.leaveDays === "" ? null : out.leaveDays;
  if (typeof out.leaveDays === "string" && out.leaveDays.trim() !== "")
    out.leaveDays = Number(out.leaveDays);
  if (typeof out.leaveDays === "number" && !Number.isFinite(out.leaveDays))
    out.leaveDays = null;

  if (typeof out.taskDone === "string") out.taskDone = out.taskDone.trim();
  if (typeof out.notes === "string") out.notes = out.notes.trim();

  // Remove null optional fields (clean request)
  if (!out.salaryType) delete out.salaryType;
  if (out.perDaySalary === null) delete out.perDaySalary;
  if (out.salaryAmount === null) delete out.salaryAmount;
  if (out.leaveDays === null) delete out.leaveDays;

  return out;
};

const labourLogService = {
  // -------------------- WORK LOGS --------------------

  /** Fetch work logs (supports either Page<> or plain array response) */
  getAllLabourLogs: async (params = {}) => {
    try {
      const res = await labourApi().get("/work-logs", {
        params: cleanParams(params),
        paramsSerializer: (p) => new URLSearchParams(p).toString(),
      });

      const data = res.data;
      if (Array.isArray(data)) {
        return {
          content: data,
          totalElements: data.length,
          totalPages: 1,
          number: 0,
          size: data.length,
        };
      }
      return data;
    } catch (e) {
      console.error("Error fetching work logs:", apiMsg(e), e);
      throw e;
    }
  },

  /** Add work log */
  addLabourLog: async (logData) => {
    try {
      const res = await labourApi().post(
        "/work-logs",
        normalizeWorkLogPayload(logData)
      );
      return res.data;
    } catch (e) {
      console.error("Error adding work log:", apiMsg(e), e);
      throw e;
    }
  },

  /** Update work log */
  updateLabourLog: async (id, logData) => {
    try {
      const res = await labourApi().put(
        `/work-logs/${id}`,
        normalizeWorkLogPayload(logData)
      );
      return res.data;
    } catch (e) {
      console.error("Error updating work log:", apiMsg(e), e);
      throw e;
    }
  },

  /** Delete work log */
  deleteLabourLog: async (id) => {
    try {
      await labourApi().delete(`/work-logs/${id}`);
    } catch (e) {
      console.error("Error deleting work log:", apiMsg(e), e);
      throw e;
    }
  },

  // -------------------- WORKERS --------------------

  getWorkers: async (activeOnly) => {
    try {
      const res = await labourApi().get("/workers", {
        params: cleanParams({ activeOnly }),
        paramsSerializer: (p) => new URLSearchParams(p).toString(),
      });

      const data = res.data;
      const list = Array.isArray(data) ? data : data?.content ?? [];
      return list.map(normalizeWorkerFromApi);
    } catch (e) {
      console.error("Error fetching workers:", apiMsg(e), e);
      throw e;
    }
  },

  createWorker: async (payload) => {
    try {
      const res = await labourApi().post(
        "/workers",
        normalizeWorkerPayload(payload)
      );
      return normalizeWorkerFromApi(res.data);
    } catch (e) {
      console.error("Error creating worker:", apiMsg(e), e);
      throw e;
    }
  },

  updateWorker: async (id, payload) => {
    try {
      const res = await labourApi().put(
        `/workers/${id}`,
        normalizeWorkerPayload(payload)
      );
      return normalizeWorkerFromApi(res.data);
    } catch (e) {
      console.error("Error updating worker:", apiMsg(e), e);
      throw e;
    }
  },

  deleteWorker: async (id) => {
    try {
      await labourApi().delete(`/workers/${id}`);
    } catch (e) {
      console.error("Error deleting worker:", apiMsg(e), e);
      throw e;
    }
  },

  // -------------------- OPTIONAL TOTAL --------------------
  getMonthlyTotal: async (month, year) => {
    try {
      const res = await labourApi().get("/work-logs/total", {
        params: cleanParams({ month, year }),
      });
      return res.data;
    } catch (e) {
      console.error("Monthly total error:", apiMsg(e), e);
      throw e;
    }
  },

  // -------------------- BATCHES --------------------

  getBatches: async () => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await axios.get(BATCHES_URL, { headers });
      return res.data;
    } catch (e) {
      console.error("Error fetching batches:", apiMsg(e), e);
      return [];
    }
  },

  _apiMsg: apiMsg,
};

export default labourLogService;